
<template>
  <div class="cheques-page" dir="rtl">
    <header class="page-header">
      <div>
        <h1>ניהול שיקים</h1>
        <p>ניהול ומעקב אחר שיקים לספקים</p>
      </div>
      <button class="primary-btn" @click="showAddForm = !showAddForm">
        {{ showAddForm ? 'ביטול' : '+ הוספת שיק' }}
      </button>
    </header>

    <!-- טופס הוספת שיק -->
    <section v-if="showAddForm" class="add-panel">
      <h2>הוספת שיק חדש</h2>
      <form @submit.prevent="addCheque">
        <div class="form-grid">
          <label>
            שם הספק / החברה
            <input v-model.trim="newCheque.dealerName"
                   required maxlength="150"
                   placeholder="שם הספק">
          </label>

          <label>
            סכום השיק (₪)
            <input v-model.number="newCheque.amount"
                   type="number" min="0.01" step="0.01" required>
          </label>

          <label>
            תאריך השיק
            <input v-model="newCheque.date" type="date" required>
          </label>

          <label>
            בנק
            <select v-model="newCheque.bank">
              <option value="">לא צוין</option>
              <option value="leumi">בנק לאומי</option>
              <option value="discount">בנק דיסקונט</option>
            </select>
          </label>

          <label class="full-width">
            הערות
            <input v-model.trim="newCheque.notes" placeholder="הערות (לא חובה)">
          </label>
        </div>

        <p v-if="formError" class="error-message">{{ formError }}</p>

        <button class="primary-btn" type="submit" :disabled="saving">
          {{ saving ? 'שומר...' : 'שמירת השיק' }}
        </button>
      </form>
    </section>

    <!-- בחירת חודש -->
    <section class="month-panel">
      <button class="month-arrow" @click="changeMonth(-1)" aria-label="החודש הקודם">
        ‹
      </button>
      <div class="month-label">{{ selectedMonthLabel }}</div>
      <button class="month-arrow" @click="changeMonth(1)" aria-label="החודש הבא">
        ›
      </button>
      <button class="today-btn" @click="goToCurrentMonth">החודש הנוכחי</button>
    </section>

    <!-- כרטיסי סטטיסטיקה -->
    <section class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon blue-icon">₪</div>
        <div>
          <p>סך כל השיקים</p>
          <h3>{{ formatAmount(stats.totalAmount) }} ₪</h3>
          <small>{{ stats.totalCount }} שיקים</small>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon orange-icon">◷</div>
        <div>
          <p>ממתינים למשיכה</p>
          <h3>{{ formatAmount(stats.pendingAmount) }} ₪</h3>
          <small>{{ stats.pendingCount }} שיקים</small>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon green-icon">✓</div>
        <div>
          <p>שיקים שנמשכו</p>
          <h3>{{ formatAmount(stats.checkedAmount) }} ₪</h3>
          <small>{{ stats.checkedCount }} שיקים</small>
        </div>
      </div>

<div class="stat-card">
  <div class="stat-icon blue-icon">₪</div>
  <div>
    <p>סכום שיקים - בנק לאומי</p>
    <h3>{{ formatAmount(stats.leumiAmount) }} ₪</h3>
    <small>{{ stats.leumiCount }} שיקים</small>
  </div>
</div>

<div class="stat-card">
  <div class="stat-icon green-icon">₪</div>
  <div>
    <p>סכום שיקים - בנק דיסקונט</p>
    <h3>{{ formatAmount(stats.discountAmount) }} ₪</h3>
    <small>{{ stats.discountCount }} שיקים</small>
  </div>
</div>

      <div class="stat-card">
        <div class="stat-icon purple-icon">∑</div>
        <div>
          <p>ממוצע לשיק</p>
          <h3>{{ formatAmount(stats.averageAmount) }} ₪</h3>
          <small>לפי החודש הנבחר</small>
        </div>
      </div>
    </section>

    <!-- חיפוש וסינון -->
    <section class="list-panel">
      <div class="list-header">
        <div>
          <h2>רשימת השיקים</h2>
          <p>השיקים לחודש {{ selectedMonthLabel }}</p>
        </div>
        <button class="refresh-btn" @click="fetchChecksForMonth" :disabled="loading">
          {{ loading ? 'טוען...' : 'רענון' }}
        </button>
      </div>

      <div class="filters">
        <input v-model.trim="searchQuery"
               type="search"
               placeholder="חיפוש לפי ספק או סכום...">

        <select v-model="bankFilter">
          <option value="">כל הבנקים</option>
          <option value="leumi">בנק לאומי</option>
          <option value="discount">בנק דיסקונט</option>
          <option value="unknown">בנק לא צוין</option>
        </select>

        <select v-model="statusFilter">
          <option value="">כל המצבים</option>
          <option value="pending">ממתינים למשיכה</option>
          <option value="checked">נמשכו</option>
        </select>
      </div>

      <div v-if="pageError" class="error-message">{{ pageError }}</div>

      <div v-if="loading" class="empty-state">טוען שיקים...</div>

      <div v-else-if="filteredChecks.length === 0" class="empty-state">
        <div class="empty-icon">₪</div>
        <h3>לא נמצאו שיקים</h3>
        <p>לא נמצאו שיקים התואמים לחיפוש ולסינון שבחרת.</p>
      </div>

      <div v-else class="cheques-grid">
        <article
          v-for="cheque in filteredChecks"
          :key="cheque.id"
          class="cheque-card"
          :class="[bankClass(cheque.bank), { 'is-checked': cheque.checked }]"
        >
          <div class="cheque-top">
            <span class="bank-badge">{{ bankLabel(cheque.bank) }}</span>
            <span class="status-badge" :class="cheque.checked ? 'status-done' : 'status-pending'">
              {{ cheque.checked ? 'נמשך' : 'ממתין למשיכה' }}
            </span>
          </div>

          <div class="cheque-amount">
            <span>סכום השיק</span>
            <strong>{{ formatAmount(cheque.amount) }} ₪</strong>
          </div>

          <div class="cheque-divider"></div>

          <div class="cheque-details">
            <div>
              <span>ספק / חברה</span>
              <strong>{{ cheque.dealerName || 'לא צוין' }}</strong>
            </div>
            <div>
              <span>תאריך השיק</span>
              <strong>{{ cheque.date }}</strong>
            </div>
            <div>
              <span>תאריך הוספה</span>
              <strong>{{ cheque.createdDate || '—' }}</strong>
            </div>
            <div v-if="cheque.notes">
              <span>הערות</span>
              <strong>{{ cheque.notes }}</strong>
            </div>
          </div>

          <div class="cheque-actions">
            <label class="status-toggle">
              <input
                type="checkbox"
                :checked="!!cheque.checked"
                :disabled="updatingIds.indexOf(cheque.id) !== -1"
                @change="toggleCheque(cheque)"
              >
              <span>{{ cheque.checked ? 'סמן כממתין' : 'סמן כנמשך' }}</span>
            </label>

            <button class="delete-btn"
                    @click="deleteDealer(cheque)"
                    :disabled="deletingIds.indexOf(cheque.id) !== -1">
              {{ deletingIds.indexOf(cheque.id) !== -1 ? 'מוחק...' : 'מחיקה' }}
            </button>
          </div>
        </article>
      </div>

      <div class="list-footer" v-if="!loading && filteredChecks.length">
        מוצגים {{ filteredChecks.length }} מתוך {{ checks.length }} שיקים
      </div>
    </section>
  </div>
</template>

<script>
import { db } from '../firebase';
import firebase from 'firebase/app';
import moment from 'moment';
import 'firebase/firestore';

export default {
  name: 'Cheques',

  data() {
    return {
      checks: [],
      selectedMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),

      searchQuery: '',
      bankFilter: '',
      statusFilter: '',

      showAddForm: false,
      saving: false,
      loading: false,
      formError: '',
      pageError: '',

      updatingIds: [],
      deletingIds: [],

      newCheque: {
        dealerName: '',
        amount: '',
        date: moment().format('YYYY-MM-DD'),
        bank: '',
        notes: ''
      }
    };
  },

  computed: {
    selectedMonthLabel() {
      return moment(this.selectedMonth).locale('he').format('MMMM YYYY');
    },

    filteredChecks() {
      const query = String(this.searchQuery || '').toLowerCase();

      return this.checks.filter((cheque) => {
        const name = String(cheque.dealerName || '').toLowerCase();
        const amount = String(cheque.amount == null ? '' : cheque.amount);
        const bank = this.normalizeBank(cheque.bank);

        const matchesSearch =
          !query || name.includes(query) || amount.includes(query);

        const matchesBank =
          !this.bankFilter ||
          (this.bankFilter === 'unknown'
            ? !bank
            : bank === this.bankFilter);

        const matchesStatus =
          !this.statusFilter ||
          (this.statusFilter === 'checked'
            ? !!cheque.checked
            : !cheque.checked);

        return matchesSearch && matchesBank && matchesStatus;
      });
    },

    stats() {
      const checks = Array.isArray(this.checks) ? this.checks : [];

      const totalAmount = checks.reduce(
        (sum, cheque) => sum + (Number(cheque.amount) || 0), 0
      );

      const checkedChecks = checks.filter(cheque => !!cheque.checked);
      const pendingChecks = checks.filter(cheque => !cheque.checked);

      const checkedAmount = checkedChecks.reduce(
        (sum, cheque) => sum + (Number(cheque.amount) || 0), 0
      );

      const pendingAmount = pendingChecks.reduce(
        (sum, cheque) => sum + (Number(cheque.amount) || 0), 0
      );

      const leumiChecks = checks.filter(
        cheque => this.normalizeBank(cheque.bank) === 'leumi'
      );

      const discountChecks = checks.filter(
        cheque => this.normalizeBank(cheque.bank) === 'discount'
      );

      const leumiAmount = leumiChecks.reduce(
        (sum, cheque) => sum + (Number(cheque.amount) || 0), 0
      );

      const discountAmount = discountChecks.reduce(
        (sum, cheque) => sum + (Number(cheque.amount) || 0), 0
      );

      return {
        totalAmount,
        totalCount: checks.length,
        checkedAmount,
        checkedCount: checkedChecks.length,
        pendingAmount,
        pendingCount: pendingChecks.length,
        averageAmount: checks.length ? totalAmount / checks.length : 0,
        leumiAmount,
        leumiCount: leumiChecks.length,
        discountAmount,
        discountCount: discountChecks.length
      };
    }
  },

  mounted() {
    this.fetchChecksForMonth();
  },

  methods: {
    normalizeBank(bank) {
      const value = String(bank || '').trim().toLowerCase();

      if (!value) return '';

      if (value === 'leumi' || value.includes('לאומי')) return 'leumi';
      if (value === 'discount' || value.includes('דיסקונט')) return 'discount';

      return value;
    },

    bankLabel(bank) {
      const normalized = this.normalizeBank(bank);

      if (normalized === 'leumi') return 'בנק לאומי';
      if (normalized === 'discount') return 'בנק דיסקונט';

      return 'בנק לא צוין';
    },

    bankClass(bank) {
      const normalized = this.normalizeBank(bank);

      if (normalized === 'leumi') return 'bank-leumi';
      if (normalized === 'discount') return 'bank-discount';

      return 'bank-unknown';
    },

    formatAmount(value) {
      return (Number(value) || 0).toLocaleString('he-IL', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
    },

    changeMonth(offset) {
      this.selectedMonth = new Date(
        this.selectedMonth.getFullYear(),
        this.selectedMonth.getMonth() + offset,
        1
      );

      this.fetchChecksForMonth();
    },

    goToCurrentMonth() {
      const today = new Date();
      this.selectedMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      this.fetchChecksForMonth();
    },

    async fetchChecksForMonth() {
      this.loading = true;
      this.pageError = '';

      const year = this.selectedMonth.getFullYear();
      const month = this.selectedMonth.getMonth();

      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 1);

      try {
        const snapshot = await db.collection('cheques')
          .where(
            'date',
            '>=',
            firebase.firestore.Timestamp.fromDate(startDate)
          )
          .where(
            'date',
            '<',
            firebase.firestore.Timestamp.fromDate(endDate)
          )
          .get();

        const loadedChecks = [];

        snapshot.forEach((doc) => {
          const data = doc.data() || {};
          const rawDate = data.date;

          let formattedDate = '';

          if (rawDate && typeof rawDate.toDate === 'function') {
            formattedDate = moment(rawDate.toDate()).format('YYYY-MM-DD');
          } else if (rawDate instanceof Date) {
            formattedDate = moment(rawDate).format('YYYY-MM-DD');
          } else if (rawDate) {
            formattedDate = String(rawDate);
          }

          loadedChecks.push({
            ...data,
            id: doc.id,
            date: formattedDate,
            amount: Number(data.amount) || 0,
            checked: !!data.checked
          });
        });

        loadedChecks.sort((a, b) => String(a.date).localeCompare(String(b.date)));
        this.checks = loadedChecks;
      } catch (error) {
        console.error('Error loading cheques:', error);
        this.pageError = 'טעינת השיקים נכשלה. בדוק את החיבור והרשאות Firebase.';
      } finally {
        this.loading = false;
      }
    },

    async addCheque() {
      this.formError = '';

      const dealerName = String(this.newCheque.dealerName || '').trim();
      const amount = Number(this.newCheque.amount);
      const dateString = this.newCheque.date;

      if (!dealerName || !dateString || !Number.isFinite(amount) || amount <= 0) {
        this.formError = 'נא למלא שם ספק, סכום ותאריך תקינים.';
        return;
      }

      const dateObject = moment(dateString, 'YYYY-MM-DD', true);

      if (!dateObject.isValid()) {
        this.formError = 'תאריך השיק אינו תקין.';
        return;
      }

      this.saving = true;

      try {
        const cheque = {
          dealerName,
          amount,
          date: firebase.firestore.Timestamp.fromDate(dateObject.toDate()),
          formattedDate: dateString,
          createdDate: moment().locale('he').format('L'),
          checked: false,
          notes: String(this.newCheque.notes || '').trim()
        };

        if (this.newCheque.bank) {
          cheque.bank = this.newCheque.bank;
        }

        await db.collection('cheques').add(cheque);

        this.newCheque = {
          dealerName: '',
          amount: '',
          date: moment().format('YYYY-MM-DD'),
          bank: '',
          notes: ''
        };

        this.showAddForm = false;

        // אם השיק أضيف لشهر آخر، انتقل إلى شهره حتى يظهر مباشرة.
        this.selectedMonth = new Date(
          dateObject.year(),
          dateObject.month(),
          1
        );

        await this.fetchChecksForMonth();
      } catch (error) {
        console.error('Error adding cheque:', error);
        this.formError = 'שמירת השיק נכשלה. נסה שוב.';
      } finally {
        this.saving = false;
      }
    },

    async toggleCheque(cheque) {
      if (!cheque || !cheque.id) return;

      const previousValue = !!cheque.checked;
      const nextValue = !previousValue;

      this.updatingIds.push(cheque.id);
      this.pageError = '';

      // עדכון מיידי בממשק, עם שחזור במקרה של כישלון.
      this.$set(cheque, 'checked', nextValue);

      try {
        await db.collection('cheques').doc(cheque.id).update({
          checked: nextValue
        });

        if (this.$root && typeof this.$root.loadChequesSum === 'function') {
          this.$root.loadChequesSum();
        }
      } catch (error) {
        console.error('Error updating cheque:', error);
        this.$set(cheque, 'checked', previousValue);
        this.pageError = 'עדכון מצב השיק נכשל. השינוי בוטל.';
      } finally {
        this.updatingIds = this.updatingIds.filter(id => id !== cheque.id);
      }
    },

    async deleteDealer(cheque) {
      if (!cheque || !cheque.id) return;

      const answer = confirm('האם אתה בטוח שברצונך למחוק את השיק?');
      if (!answer) return;

      this.deletingIds.push(cheque.id);
      this.pageError = '';

      try {
        await db.collection('cheques').doc(cheque.id).delete();

        this.checks = this.checks.filter(item => item.id !== cheque.id);

        if (this.$root && typeof this.$root.loadChequesSum === 'function') {
          this.$root.loadChequesSum();
        }
      } catch (error) {
        console.error('Error deleting cheque:', error);
        this.pageError = 'מחיקת השיק נכשלה.';
      } finally {
        this.deletingIds = this.deletingIds.filter(id => id !== cheque.id);
      }
    }
  }
};
</script>

<style scoped>
.cheques-page {
  direction: rtl;
  padding: 24px;
  color: #202938;
  min-height: 100%;
  background: #f5f7fb;
  font-family: Arial, sans-serif;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdfa 100%);
  border: 1px solid #dcebe8;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 65, 60, 0.06);
  color: #064e49;
}

.page-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #064e49;
}

.page-header p {
  margin: 6px 0 0;
  font-size: 14px;
  color: #718096;
}

.page-header .btn-cancel,
.page-header .cancel-btn {
  background: #e8f5f2;
  color: #065f56;
  border: 1px solid #cce8e1;
  border-radius: 10px;
  padding: 12px 22px;
  font-weight: 700;
  transition: all 0.2s ease;
}

.page-header .btn-cancel:hover,
.page-header .cancel-btn:hover {
  background: #d5eee7;
  transform: translateY(-1px);
}

.page-header p,
.list-header p {
  margin: 0;
  color: #7a8495;
  font-size: 14px;
}

.primary-btn {
  border: 0;
  border-radius: 10px;
  padding: 12px 20px;
  background: #315efb;
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  transition: background .2s, transform .2s;
}

.primary-btn:hover {
  background: #2449d8;
}

.primary-btn:disabled,
.delete-btn:disabled,
.refresh-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.add-panel,
.month-panel,
.list-panel {
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 16px;
  padding: 22px;
  margin-bottom: 22px;
  box-shadow: 0 5px 22px rgba(31, 45, 75, .035);
}

.add-panel h2,
.list-header h2 {
  margin: 0 0 8px;
  font-size: 19px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin: 20px 0;
}

.form-grid label {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #4e596b;
}

.form-grid input,
.form-grid select,
.filters input,
.filters select {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: 1px solid #dfe5ef;
  border-radius: 9px;
  padding: 12px;
  color: #202938;
  background: #fff;
  font: inherit;
  outline: none;
}

.form-grid input:focus,
.form-grid select:focus,
.filters input:focus,
.filters select:focus {
  border-color: #315efb;
  box-shadow: 0 0 0 3px rgba(49, 94, 251, .09);
}

.full-width {
  grid-column: 1 / -1;
}

.month-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.month-label {
  min-width: 150px;
  text-align: center;
  font-size: 19px;
  font-weight: 800;
  text-transform: capitalize;
}

.month-arrow,
.today-btn,
.refresh-btn {
  border: 1px solid #e1e7f1;
  border-radius: 9px;
  background: #fff;
  color: #344054;
  padding: 9px 13px;
  cursor: pointer;
}

.month-arrow {
  width: 40px;
  height: 40px;
  font-size: 25px;
}

.today-btn {
  margin-right: 10px;
  color: #315efb;
  font-weight: 700;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 15px;
  padding: 20px 16px;
  min-width: 0;
  box-shadow: 0 5px 22px rgba(31, 45, 75, .035);
}

.stat-icon {
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 23px;
  font-weight: 800;
}

.blue-icon {
  color: #315efb;
  background: #edf2ff;
}

.orange-icon {
  color: #d97706;
  background: #fff4df;
}

.green-icon {
  color: #13875a;
  background: #e6f8ef;
}

.purple-icon {
  color: #7950c8;
  background: #f1eaff;
}

.stat-card p {
  margin: 0 0 8px;
  color: #778195;
  font-size: 13px;
}

.stat-card h3 {
  margin: 0 0 5px;
  font-size: 20px;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.stat-card small {
  color: #8992a2;
}

.list-header {
  margin-bottom: 22px;
}

.refresh-btn {
  color: #315efb;
  font-weight: 700;
}

.filters {
  display: grid;
  grid-template-columns: minmax(200px, 1fr) 190px 190px;
  gap: 12px;
  margin-bottom: 22px;
}

.cheques-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.cheque-card {
  position: relative;
  min-width: 0;
  background: #fff;
  border: 1px solid #e1e7f0;
  border-right: 5px solid #a8b0bd;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 5px 18px rgba(31, 45, 75, .04);
  transition: box-shadow .2s, transform .2s;
}

.cheque-card:hover {
  box-shadow: 0 9px 26px rgba(31, 45, 75, .09);
  transform: translateY(-2px);
}

.bank-leumi {
  border-right-color: #2670e8;
}

.bank-discount {
  border-right-color: #1b9a67;
}

.bank-unknown {
  border-right-color: #a8b0bd;
}

.cheque-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.bank-badge,
.status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 7px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 800;
}

.bank-leumi .bank-badge {
  color: #1c5fc7;
  background: #eaf2ff;
}

.bank-discount .bank-badge {
  color: #147a51;
  background: #e6f8ef;
}

.bank-unknown .bank-badge {
  color: #697386;
  background: #f0f2f5;
}

.status-done {
  color: #147a51;
  background: #e6f8ef;
}

.status-pending {
  color: #a45b00;
  background: #fff3dc;
}

.cheque-amount {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 23px 0 18px;
}

.cheque-amount span,
.cheque-details span {
  color: #7b8495;
  font-size: 12px;
}

.cheque-amount strong {
  font-size: 29px;
  font-weight: 800;
  color: #1e293b;
  overflow-wrap: anywhere;
}

.cheque-divider {
  border-top: 1px dashed #dfe5ef;
  margin-bottom: 18px;
}

.cheque-details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.cheque-details > div {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
}

.cheque-details strong {
  font-size: 14px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.is-checked .cheque-amount strong {
  color: #16845a;
}

.cheque-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid #edf0f5;
  margin-top: 20px;
  padding-top: 15px;
}

.status-toggle {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  color: #4e596b;
  font-size: 13px;
  font-weight: 700;
}

.status-toggle input {
  width: 17px;
  height: 17px;
  accent-color: #16845a;
  cursor: pointer;
}

.status-toggle input:disabled {
  cursor: wait;
}

.delete-btn {
  border: 1px solid #ffd6d6;
  border-radius: 8px;
  background: #fff5f5;
  color: #d33;
  padding: 8px 13px;
  font-weight: 700;
  cursor: pointer;
}

.delete-btn:hover {
  background: #ffe7e7;
}

.empty-state {
  padding: 55px 20px;
  text-align: center;
  color: #778195;
}

.empty-state h3 {
  color: #344054;
  margin: 12px 0 8px;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

.empty-icon {
  width: 52px;
  height: 52px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  background: #edf2ff;
  color: #315efb;
  font-size: 25px;
  font-weight: 800;
}

.error-message {
  background: #fff0f0;
  color: #b42318;
  border: 1px solid #ffd6d6;
  border-radius: 9px;
  padding: 12px;
  margin: 12px 0;
  font-size: 14px;
}

.list-footer {
  border-top: 1px solid #edf0f5;
  margin-top: 20px;
  padding-top: 15px;
  color: #7b8495;
  font-size: 13px;
}

@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .cheques-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 650px) {
  .cheques-page {
    padding: 14px;
  }

  .page-header {
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 23px;
  }

  .primary-btn {
    padding: 10px 13px;
    white-space: nowrap;
  }

  .form-grid,
  .stats-grid,
  .filters {
    grid-template-columns: 1fr;
  }

  .month-panel {
    flex-wrap: wrap;
    gap: 10px;
    padding: 15px;
  }

  .month-label {
    min-width: 120px;
    font-size: 16px;
  }

  .today-btn {
    margin-right: 0;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-card h3 {
    font-size: 19px;
  }

  .list-panel,
  .add-panel {
    padding: 15px;
  }

  .list-header {
    align-items: flex-start;
  }

  .cheque-card {
    padding: 16px;
  }

  .cheque-amount strong {
    font-size: 25px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/cheques.vue